import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import avatarFallback from '../../../../../assets/images/profile.png';
import { Calendar, Crosshair, Target, MapPin, Package, ArrowLeft, Lock, User } from 'lucide-react';
import { ApiError } from '../../../../../platform/api/apiClient';
import { profileService } from '../../../../../services';
import './ProfileView.css';

const ProfileView = () => {
    const { friendUsername } = useParams();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const isLegacyId = /^\d+$/.test(friendUsername || '');
                const data = isLegacyId
                    ? await profileService.getPublicProfileById(friendUsername)
                    : await profileService.getPublicProfileByUsername(friendUsername);
                setProfile(data);
            } catch (error) {
                if (error instanceof ApiError) {
                    if (error.status === 403) {
                        setAccessDenied(true);
                    } else if (error.status === 404) {
                        setNotFound(true);
                    } else {
                        console.error('Unexpected profile response:', error.status, error.data);
                        setAccessDenied(true);
                    }
                } else {
                    console.error('Error fetching profile:', error);
                    setAccessDenied(true);
                }
            } finally {
                setLoading(false);
            }
        };

        if (friendUsername) fetchProfile();
    }, [friendUsername]);

    if (loading) {
        return (
            <div className="profile-view-page">
                <div className="profile-view-loading">
                    <div className="profile-view-loading-spinner"></div>
                    <p>Loading profile...</p>
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="profile-view-page">
                <div className="profile-view-error">
                    <div className="profile-view-error-icon">
                        <User size={48} />
                    </div>
                    <h2>Profile Not Found</h2>
                    <p>This profile could not be found or may have been removed.</p>
                    <Link to="/dashboard/friends" className="profile-view-back-btn">
                        <ArrowLeft size={18} />
                        Back to Friends
                    </Link>
                </div>
            </div>
        );
    }

    if (accessDenied) {
        return (
            <div className="profile-view-page">
                <div className="profile-view-error">
                    <div className="profile-view-error-icon profile-view-error-private">
                        <Lock size={48} />
                    </div>
                    <h2>Private Profile</h2>
                    <p>This user's profile is private and cannot be viewed.</p>
                    <Link to="/dashboard/friends" className="profile-view-back-btn">
                        <ArrowLeft size={18} />
                        Back to Friends
                    </Link>
                </div>
            </div>
        );
    }

    const {
        profilePictureUrl,
        firstName,
        lastName,
        userName,
        shareShootingScores,
        shareLastShoots,
        shareClubMemberships,
        shareEquipmentDetails
    } = profile;

    const performanceCards = [];

    if (shareLastShoots) {
        performanceCards.push({
            key: 'lastShotWith',
            label: 'Last Shot With',
            value: profile.lastShotWith || null,
            icon: Target,
            tone: 'orange',
            emptyMessage: 'No recent shoot partner logged yet.'
        });
    }

    if (shareShootingScores) {
        performanceCards.push({
            key: 'accuracy',
            label: 'Accuracy',
            value: profile.accuracyPercentage || null,
            icon: Crosshair,
            tone: 'green',
            emptyMessage: 'No score history published yet.'
        });
    }

    if (shareLastShoots) {
        performanceCards.push({
            key: 'lastEventDate',
            label: 'Last Event Date',
            value: profile.lastEventDate || null,
            icon: Calendar,
            tone: 'blue',
            emptyMessage: 'No event activity published yet.'
        });
    }

    const hasAnySharedSections = performanceCards.length > 0 || shareClubMemberships || shareEquipmentDetails;

    return (
        <div className="profile-view-page">
            {/* Header */}
            <div className="profile-view-header">
                <Link to="/dashboard/friends" className="profile-view-back-link">
                    <ArrowLeft size={20} />
                    Back to Friends
                </Link>
                <div className="profile-view-header-content">
                    <h1>Shooter Profile</h1>
                    <p>Public performance information</p>
                </div>
            </div>

            {/* Profile Card */}
            <div className="profile-view-content">
                <div className="profile-view-sidebar">
                    <div className="profile-view-card">
                        <div className="profile-view-avatar">
                            <img
                                src={profilePictureUrl || avatarFallback}
                                alt={`${firstName} ${lastName}`}
                            />
                        </div>
                        <h2 className="profile-view-name">{firstName} {lastName}</h2>
                        <span className="profile-view-username">@{userName}</span>
                        <div className="profile-view-divider"></div>
                        <div className="profile-view-badge">
                            <User size={14} />
                            Active Shooter
                        </div>
                    </div>
                </div>

                <div className="profile-view-main">
                    {performanceCards.length > 0 && (
                        <div className="profile-view-section">
                            <h3 className="profile-view-section-title">Performance Stats</h3>
                            <div className="profile-view-stats-grid">
                                {performanceCards.map((card) => {
                                    const CardIcon = card.icon;
                                    return (
                                        <div className="profile-view-stat-card" key={card.key}>
                                            <div className={`profile-view-stat-icon profile-view-stat-${card.tone}`}>
                                                <CardIcon size={22} />
                                            </div>
                                            <div className="profile-view-stat-info">
                                                <span className="profile-view-stat-label">{card.label}</span>
                                                <span className="profile-view-stat-value">
                                                    {card.value || <span className="profile-view-stat-empty">{card.emptyMessage}</span>}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {shareClubMemberships && (
                        <div className="profile-view-section">
                            <h3 className="profile-view-section-title">
                                <MapPin size={20} />
                                Club Memberships
                            </h3>
                            <div className="profile-view-placeholder">
                                <p>No club memberships shared</p>
                            </div>
                        </div>
                    )}

                    {shareEquipmentDetails && (
                        <div className="profile-view-section">
                            <h3 className="profile-view-section-title">
                                <Package size={20} />
                                Equipment
                            </h3>
                            <div className="profile-view-placeholder">
                                <p>No equipment details shared</p>
                            </div>
                        </div>
                    )}

                    {!hasAnySharedSections && (
                        <div className="profile-view-section">
                            <h3 className="profile-view-section-title">Shared Profile</h3>
                            <div className="profile-view-placeholder">
                                <p>This shooter is keeping profile details private right now.</p>
                            </div>
                        </div>
                    )}

                    <div className="profile-view-share-note">
                        Public shareable profile links for non-members are planned in a future update.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileView;
